import { ErrorToast, InfoToast } from '@services/utils/ToastUtils';
import { theme } from '@theme';
import { FC, useEffect, useState } from 'react';
import styled from 'styled-components';
import { useInterval } from '@hooks/useInterval';
import { DealNoteService } from '@services/portfolio/DealNoteService';
import { formatDateTime } from '@services/utils/DateUtils';
import { NoteView } from '@appTypes/Note';
import { CrmStatus, CrmType, getCrmName, isCrmReactive } from './crmData';
import NoteWarning from './NoteWarning';
import { isCrmIntegrated } from '@components/dealsheet/shared/notes-modal/crm-data';
import { Icon, Typography } from '@components/style';

export type NoteProps = {
  warning?: string;
  id: number | string;
  createdAt: Date;
  offerNotePushRetry?: boolean;
  source: number;
  content: string;
  createdBy: string;
  crmType?: CrmType;
  crmStatus?: CrmStatus;
  isNewlyAdded?: boolean;
  openRetryInfo?: () => void;
  dealerId: string;
  updateSingleNote: (newItem: NoteView, id: number) => void;
  addPendingNoteId: (id: number) => void;
  removePendingNoteId: (id: number) => void;
  retryAction: (
    id: string | number,
    dealerId: string,
    addMissingData: boolean,
    crmType: CrmType,
  ) => void;
  resetRetryState: () => void;
  removeNewNote: (id: number) => void;
  setSavingNoteCrmType: (crmType: CrmType | undefined) => void;
};

const NoteComponent: FC<NoteProps> = ({
  warning,
  id,
  createdAt,
  offerNotePushRetry,
  source,
  content,
  createdBy,
  crmType,
  crmStatus,
  isNewlyAdded,
  openRetryInfo,
  dealerId,
  updateSingleNote,
  addPendingNoteId,
  removePendingNoteId,
  retryAction,
  resetRetryState,
  removeNewNote,
  setSavingNoteCrmType,
}) => {
  const [retrySent, setRetrySent] = useState(false);
  const [shouldPollingBeExecuted, setShouldPollingBeExecuted] = useState(false);
  const [doNotCreateOpportunitySelected, setDoNotCreateOpportunitySelected] = useState(false);
  const [missingDataRetried, setMissingDataRetried] = useState(false);
  const [fetchingError, setFetchingError] = useState(false);
  const noteDate = new Date(createdAt!);
  const formattedDate = formatDateTime(noteDate);

  useEffect(() => {
    const pending = isCrmReactive(crmType) && (crmStatus === CrmStatus.Pending || retrySent);
    setShouldPollingBeExecuted(pending);
    if (pending) {
      setSavingNoteCrmType(crmType);
      addPendingNoteId(id as number);
    } else {
      setSavingNoteCrmType(undefined);
      removePendingNoteId(id as number);
    }
  }, [
    crmStatus,
    crmType,
    id,
    retrySent,
    addPendingNoteId,
    removePendingNoteId,
    setShouldPollingBeExecuted,
  ]);

  useInterval<() => void>(
    () => {
      DealNoteService.getNote(id as number, dealerId)
        .then((res) => {
          updateSingleNote(res, id as number);
          handleRetryResponse(res.crmStatus, retrySent);
        })
        .catch(() => {
          removePendingNoteId(id as number);
          setSavingNoteCrmType(undefined);
          setShouldPollingBeExecuted(false);
          setFetchingError(true);
          const errorMessage = isCrmReactive(crmType)
            ? `An error has occurred during posting note to ${getCrmName(crmType)}`
            : 'An error has occurred during posting note';
          ErrorToast(errorMessage);
        });
    },
    2000,
    shouldPollingBeExecuted,
  );

  const shouldShowWarning = !!warning || offerNotePushRetry;

  const shouldShowOpportunityButtons =
    crmStatus === CrmStatus.MissingData &&
    isCrmReactive(crmType) &&
    !doNotCreateOpportunitySelected &&
    ((isNewlyAdded && !missingDataRetried) || (!isNewlyAdded && missingDataRetried));

  const shouldShowSuccessInfo = isCrmIntegrated(crmType) && (crmStatus === CrmStatus.Success);

  const getWarningText = () => {
    if (!shouldShowOpportunityButtons) return warning;
    if (crmType == CrmType.Eleads)
      return 'There is no opportunity for this customer in CDK CRM. Would you like to create an opportunity and retry sending the note?';
    if (crmType == CrmType.DealerSocket)
      return 'There is no live Sales Event for this customer in DealerSocket. Would you like to create Sales Event and retry sending a note?';
  };

  const warningText = getWarningText();

  const showRetryButton = () => {
    if (isCrmReactive(crmType)) {
      return (
        (crmStatus === CrmStatus.Failed && offerNotePushRetry) ||
        (crmStatus === CrmStatus.MissingData && !shouldShowOpportunityButtons)
      );
    }
    return offerNotePushRetry;
  };

  const shouldShowRetryButton = showRetryButton();

  const handleCopyButtonClick = async (content: string) => {
    await navigator.clipboard.writeText(content);
    InfoToast('Note copied to clipboard: ' + content);
  };

  const onRetryClick = (addMissingData: boolean) => {
    setMissingDataRetried(false);
    retryAction(id, dealerId, addMissingData, crmType);
    setRetrySent(true);
  };

  const onDontCreateClick = () => {
    setDoNotCreateOpportunitySelected(true);
  };

  const handleRetryResponse = (crmStatus: CrmStatus, retrySent: boolean) => {
    if (crmStatus !== CrmStatus.Pending && retrySent) {
      setRetrySent(false);
      resetRetryState();
      if (isNewlyAdded) {
        removeNewNote(id as number);
      }
      if (crmStatus === CrmStatus.MissingData) {
        setMissingDataRetried(true);
        setDoNotCreateOpportunitySelected(false);
      }
    }
  };

  return (
    <NoteContainer
      key={id}
      data-testid='note'
      hidden={crmStatus === 1 && isNewlyAdded && !fetchingError}
    >
      <div>
        <NoteDate
          size={12}
          data-testid='note-date'
          fontWeight={theme.typography.fontWeights.semibold}
        >
          {formattedDate}
        </NoteDate>
        <UserName
          size={12}
          data-testid='note-username'
          fontWeight={theme.typography.fontWeights.semibold}
        >
          {createdBy}
        </UserName>
        {source === 1 && (
          <ViaCrm title='Syndicated from Momentum CRM' data-testid='via-crm'>
            Via Momentum
          </ViaCrm>
        )}
        <NoteHeaderActions>
          <IconButton data-testid='copy-button' onClick={() => handleCopyButtonClick(content)}>
            <Icon type='copy' size={15} />
            Copy
          </IconButton>
        </NoteHeaderActions>
      </div>
      <NoteContentWrapper data-testid='note-content'>
        <NoteContent size={16} marginBottom={10}>
          {content}
        </NoteContent>
      </NoteContentWrapper>
      {shouldShowWarning && (
        <NoteWarning
          warning={warningText}
          showRetryButton={shouldShowRetryButton}
          onRetryClick={onRetryClick}
          retryButtonDisabled={retrySent}
          showManualOppButtons={shouldShowOpportunityButtons}
          onDontCreateClick={onDontCreateClick}
          retrySent={retrySent}
          crmType={crmType}
          openRetryInfo={openRetryInfo}
        />
      )}
      {shouldShowSuccessInfo && Success(getCrmName(crmType))}
    </NoteContainer>
  );
};

export default NoteComponent;

const Success = (crmName: string) => (
  <SuccessWrapper data-testid='success'>
    <Icon type='check-circle' size={22} color={theme.palette.darkPurpleEUI} />
    <Typography size={14} color={theme.palette.dusty} fontStyle='italic' marginLeft={5}>
      Note successfully pushed to {crmName}.
    </Typography>
  </SuccessWrapper>
);

const NoteDate = styled(Typography)`
  margin-right: 6px;
  color: ${theme.palette.basicGray};
`;

const UserName = styled(Typography)`
  color: ${theme.palette.dusty};
  text-transform: uppercase;
`;

const ViaCrm = styled.span`
  color: ${theme.palette.lightGreyTwo};
  background-color: ${theme.palette.lightGrayThree};
  display: inline-block;
  padding: 3px 6px;
  border-radius: 3px;
  line-height: 1;
  margin-left: 4px;
  font-weight: 600;
  font-size: 12px;
  text-transform: uppercase;
`;

const NoteContent = styled(Typography)`
  color: ${theme.palette.wetAsphalt};
  font-weight: ${theme.typography.fontWeights.normal};
  word-break: break-word;
`;

const NoteContentWrapper = styled.div`
  padding-top: 10px;
`;

interface INoteContainer {
  hidden: boolean;
}

const NoteContainer = styled.div<INoteContainer>`
        border-top: 1px solid ${theme.palette.background};
        border-bottom: 1px solid ${theme.palette.background};
        width: 100%;
        padding: 20px;
        visibility: ${(props) => (props.hidden ? 'hidden' : 'visible')};
        :nth-child(odd) {
          background-color: ${theme.palette.translucentPink};
        };
        :last-child {
          border-bottom: none;
        };
}
`;

const NoteHeaderActions = styled.div`
  display: inline-block;
  float: right;
`;

const SuccessWrapper = styled.span`
  display: inline-flex;
  margin-left: auto;
  opacity: 0.7;
  align-items: self-end;
  margin-top: 10px;
`;

const IconButton = styled.button`
  opacity: 0.5;
  cursor: pointer;
  text-transform: uppercase;
  border: 1px solid ${theme.palette.lightGrayTwo};
  padding: 4px 5px 3px;
  margin-left: 5px;
  font-size: 12px;
  line-height: 1.2;
  color: ${theme.palette.lightGrayTwo};
  border-radius: 3px;
  white-space: nowrap;
  background-color: white;
  span {
    padding-right: 5px;
    vertical-align: bottom;
  }

  &:hover,
  &.zeroclipboard-is-hover {
    color: ${theme.palette.darkGray};
    border: 1px solid ${theme.palette.darkGray};
    opacity: 1;
  }

  @media (max-width: 991px) {
    display: none !important;
  }
`;
